import type { CancelMultipleQuotesDeps } from '@src/components/OMS/NewRFQ/cancelMultipleQuotes';
import { useTradingSettings } from '@src/providers/AppConfigProvider';
import {
  type AgGridMenuItems,
  type FilterableProperty,
  filterByCellValueMenuItem,
  type Leaves,
  useCallbackRef,
  type UseFilterBuilderOutput,
  useMixpanel,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, IRowNode } from 'ag-grid-community';
import { compact, isNil } from 'lodash-es';
import { type ReactNode, useMemo } from 'react';
import { getCopyFundQuotePricesItem } from '../Quotes/MenuItems/copyFundQuotePricesItem';
import { useCancelOpenQuotesMenuItem } from '../Quotes/MenuItems/useCancelOpenQuotesMenuItem';
import { useQuoteContextMenuActions } from '../Quotes/MenuItems/useQuoteContextMenuActions';
import { predictionMarketColIDToFilterKey } from '../predictionMarketBlotter';
import { QuoteRow, type QuotesBlotterRow } from './types';
import type { QuotesV2TableFilter } from './useQuotesV2Filter';

export function useQuotesV2Menu({
  openClause,
  filterableProperties,
  quoteSub,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
  quoteSub: CancelMultipleQuotesDeps['quoteSub'];
}): {
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<QuotesBlotterRow>) => AgGridMenuItems;
  dialogs: ReactNode;
} {
  const mixpanel = useMixpanel();

  const { getQuoteActionMenuItems, dialog: quoteActionDialog } = useQuoteContextMenuActions();
  const { rfqBestQuoteBy } = useTradingSettings();
  const { getCancelOpenQuotesMenuItem, dialog: cancelOpenQuotesDialog } = useCancelOpenQuotesMenuItem({ quoteSub });

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<QuotesBlotterRow>) => {
    const row = params?.node?.data;
    if (isNil(row)) {
      return [];
    }

    const quote = row instanceof QuoteRow ? row.quote : undefined;
    const copyPricingItem = getCopyFundQuotePricesItem(getSelectedQuotesWithParentCOs(params), rfqBestQuoteBy);

    return compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      copyPricingItem,
      getCancelOpenQuotesMenuItem(row),
      ...(quote ? getQuoteActionMenuItems(quote) : []),
    ]);
  });

  const dialogs = useMemo(
    () => (
      <>
        {quoteActionDialog}
        {cancelOpenQuotesDialog}
      </>
    ),
    [quoteActionDialog, cancelOpenQuotesDialog]
  );

  return {
    getCustomContextMenuItems,
    dialogs,
  };
}

function getSelectedQuotesWithParentCOs(
  params: GetContextMenuItemsParams<QuotesBlotterRow>
): Parameters<typeof getCopyFundQuotePricesItem>[0] {
  const selectedQuoteNodes = params.api
    .getSelectedNodes()
    .filter((node): node is IRowNode<QuotesBlotterRow> & { data: QuoteRow } => node.data instanceof QuoteRow);

  return selectedQuoteNodes.map(node => ({
    quote: node.data.quote,
    parentCO: node.data.quote.careOrder,
  }));
}

export function colIDToFilterBuilderKey(id: string): keyof QuotesV2TableFilter | undefined {
  switch (id as Leaves<QuotesBlotterRow>) {
    case 'Symbol':
      return 'Symbols';
    case 'quote.subAccounts':
      return 'SubAccounts';
    case 'quote.tradedMarkets':
      return 'Markets';
    case 'Side':
      return 'Sides';
    case 'quote.TradedSide':
      return 'TradedSides';
    case 'QuoteStatus':
      return '_statuses';
    case 'quote.User':
      return 'Users';
    case 'quote.RFQID':
      return 'RFQID';
    case 'quote.ParentRFQID':
      return 'ParentRFQID';
  }
  return predictionMarketColIDToFilterKey(id);
}
